// ============================================================================
// lib/mailAccess.ts — доступ к приложению «Почта» (SSoT на клиенте — сервер).
//
// Правило (то же, что в серверном гейте handler/mail_access.go):
//   - администратор (роль admin / global:admin) — всегда;
//   - остальные — только при явном полномочии mail_access у пользователя или
//     у одной из его ролей (реестр полномочий, выдаётся в «Пользователях»/
//     «Ролях» → вкладка «Полномочия»);
//   - по умолчанию полномочие ВЫКЛ (почта только у администратора).
//
// Клиент НЕ угадывает права по ролям: единственный источник — серверный
// ResolvePermission (учитывает пользователя, роли и default). Кэш — по паре
// «база + пользователь», чтобы док/«Пуск» не дёргали RPC на каждый рендер.
// ============================================================================

import { useEffect, useState } from 'react';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { useSecurityAccess } from '@/lib/securityRoles';
import { logSwallowed } from '@/lib/errors';

/** Код полномочия доступа к почте (совпадает с metadata.MailAccessPermissionCode). */
export const MAIL_ACCESS_PERMISSION = 'mail_access';

export type MailAccessState = 'loading' | 'allowed' | 'denied';

const accessCache = new Map<string, boolean>();
const inFlight = new Map<string, Promise<boolean>>();

function isTruthy(value: unknown): boolean {
  const text = String(value ?? '').trim().toLowerCase();
  return text === 'true' || text === '1' || text === 'yes';
}

async function resolveMailAccess(): Promise<boolean> {
  try {
    const res: any = await getSDK().metadata.resolvePermission({ code: MAIL_ACCESS_PERMISSION } as any);
    return isTruthy(res?.value);
  } catch (e) {
    logSwallowed(e, 'lib/mailAccess.ts:resolve');
    return false;
  }
}

/** useMailAccessState — состояние доступа (loading → allowed/denied). */
export function useMailAccessState(): MailAccessState {
  const access = useSecurityAccess();
  const userId = useAuthStore((s) => String(s.user?.id || ''));
  // Токен в ключе кэша: новый вход (другой токен) не должен переиспользовать
  // результат прошлой сессии, если полномочие успели отозвать.
  const token = useAuthStore((s) => s.token || '');
  const baseSlug = useCenterStore((s) => s.baseSlug || 'local');
  const cacheKey = `${baseSlug}:${userId}:${token}`;

  const [state, setState] = useState<MailAccessState>(() => {
    if (access.isAdmin) return 'allowed';
    if (!userId) return 'denied';
    const cached = accessCache.get(cacheKey);
    return cached === undefined ? 'loading' : cached ? 'allowed' : 'denied';
  });

  useEffect(() => {
    if (access.isAdmin) {
      setState('allowed');
      return;
    }
    if (!userId) {
      setState('denied');
      return;
    }
    const cached = accessCache.get(cacheKey);
    if (cached !== undefined) {
      setState(cached ? 'allowed' : 'denied');
      return;
    }
    let cancelled = false;
    let pending = inFlight.get(cacheKey);
    if (!pending) {
      pending = resolveMailAccess();
      inFlight.set(cacheKey, pending);
      void pending.finally(() => inFlight.delete(cacheKey));
    }
    void pending.then((allowed) => {
      accessCache.set(cacheKey, allowed);
      if (!cancelled) setState(allowed ? 'allowed' : 'denied');
    });
    return () => { cancelled = true; };
  }, [access.isAdmin, cacheKey, userId]);

  return state;
}

/** useMailAccess — булево для правил видимости (false, пока идёт проверка). */
export function useMailAccess(): boolean {
  return useMailAccessState() === 'allowed';
}
